import get from 'lodash/get';
import { trimSearch } from '@xmatters/common/utils/searchUtil';
import bluebird from 'utils/bluebird';

import { getListingURIGenerator } from '@xmatters/redux/actions/listPage/utils';
import { getCompleteAddress } from '../../pages/SiteListPage/util';

export const IS_FORM_DIRTY = 'IS_FORM_DIRTY';
export const SHOW_CHICKEN_EXIT = 'SHOW_CHICKEN_EXIT';

export const SITE_LIST = `SITE_LIST`;
export const SITE_LIST_CANCEL_ACTION = `${SITE_LIST}_CANCEL_ACTION`;
export const SITE_LIST_REFRESH = `${SITE_LIST}_REFRESH`;
export const LOAD_SITES = `LOAD_SITES`;
export const LOAD_SITES_START = `${LOAD_SITES}_START`;
export const LOAD_SITES_SUCCESS = `${LOAD_SITES}_SUCCESS`;
export const LOAD_SITES_FAIL = `${LOAD_SITES}_FAIL`;
export const DELETE_SITE = `DELETE_SITE`;
export const DELETE_SITES = 'DELETE_SITES';
export const DELETE_SITES_START = `${DELETE_SITES}_START`;
export const DELETE_SITES_SUCCESS = `${DELETE_SITES}_SUCCESS`;
export const DELETE_SITES_FAIL = `${DELETE_SITES}_FAIL`;
export const GEOCODE_SITES = `GEOCODE_SITES`;
export const GEOCODE_SITES_START = `${GEOCODE_SITES}_START`;
export const GEOCODE_SITES_SUCCESS = `${GEOCODE_SITES}_SUCCESS`;
export const GEOCODE_SITES_FAIL = `${GEOCODE_SITES}_FAIL`;
export const PROCESSING_SITES_ONTO = `PROCESSING_SITE_ONTO`;
export const SITE_LIST_SET_OPERATION = `SITE_LIST_SET_OPERATION`;
export const UPDATE_SITE = `UPDATE_SITE`;

export const getSearchFilterParams = props => {
  const { startIndex = 0, stopIndex = 0, search, sortBy, sortOrder } = props;

  const status = get(props, 'status.value');
  const countries = get(props, 'country.value');
  const geocoded = get(props, 'geocoded.value');

  const params = {
    embed: 'permittedActions',
    sortOrder: sortOrder ? sortOrder.toUpperCase() : undefined,
    sortBy: sortBy ? sortBy.toUpperCase() : undefined,
  };

  if (sortBy && sortOrder) {
    params.sortBy = sortBy.toUpperCase();
    params.sortOrder = sortOrder.toUpperCase();
  }

  params.limit = Math.max(100, stopIndex - startIndex);
  params.offset = startIndex;

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = trimmedSearchText;
    params.operand = 'AND';
  }

  if (status && status.length) {
    params.status = status;
  }

  if (countries && countries.length) {
    params.country = countries;
  }

  if (geocoded && geocoded.length) {
    params.geocoded = geocoded[0] === 'GEOCODED';
  }

  return params;
};

export function searchSites(props = {}) {
  const params = getSearchFilterParams(props);

  return {
    type: LOAD_SITES,
    meta: {
      xmApi: true,
      uri: '/webapi/xm/1/sites',
      params,
      method: 'get',
      customUriWithParamsGenerator: getListingURIGenerator,
    },
  };
}

export function getAllSites() {
  return {
    type: LOAD_SITES,
    meta: {
      xmApi: true,
      uri: '/webapi/xm/1/sites',
      method: 'getall',
      params: {
        limit: 1000,
      },
      customUriWithParamsGenerator: getListingURIGenerator,
    },
  };
}

export const EXPORT_SITES = 'EXPORT_SITES';
export const EXPORT_SITES_START = `${EXPORT_SITES}_START`;
export const EXPORT_SITES_SUCCESS = `${EXPORT_SITES}_SUCCESS`;
export const EXPORT_SITES_FAIL = `${EXPORT_SITES}_FAIL`;
export const EXPORT_SITES_FINISHED = `${EXPORT_SITES}_FINISHED`;

export const exportSites = ({ filters }) => {
  const params = {
    limit: 1000,
    ...getSearchFilterParams(filters),
  };

  return {
    type: EXPORT_SITES,
    meta: {
      xmApi: true,
      method: 'getall',
      uri: '/webapi/xm/1/sites',
      params,
      customUriWithParamsGenerator: getListingURIGenerator,
    },
  };
};

export const exportSitesFinished = () => ({
  type: EXPORT_SITES_FINISHED,
});

export function geocodeSite(googleMaps, address, country) {
  return new Promise((resolve, reject) => {
    new googleMaps.Geocoder().geocode(
      { address, componentRestrictions: { country } },
      (results, status) => {
        if (status === googleMaps.GeocoderStatus.OK) {
          if (results.length > 1) {
            reject('geocodeMultiResults');
          }
          if (results.length === 1) {
            resolve(results[0].geometry.location);
          }
        } else if (status === googleMaps.GeocoderStatus.ZERO_RESULTS) {
          reject('geocodeNoResults');
        } else {
          reject('geocodeError');
        }
      },
    );
  });
}

export function updateSite(params) {
  return {
    type: UPDATE_SITE,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/sites`,
      method: 'post',
      params,
    },
  };
}

export function deleteSite(uuid) {
  return {
    type: DELETE_SITE,
    meta: {
      xmApi: true,
      uri: `/webapi/xm/1/sites/${uuid}`,
      method: 'del',
    },
  };
}

export const deleteSites = groupUuids => (dispatch, getState) => {
  const onSuccess = results => {
    dispatch({ type: DELETE_SITES_SUCCESS });
    return results;
  };
  const onFail = e => {
    dispatch({ type: DELETE_SITES_FAIL });
    throw e;
  };

  dispatch({ type: PROCESSING_SITES_ONTO, payload: null });
  dispatch({ type: DELETE_SITES_START });

  return bluebird
    .mapSeries(groupUuids, uuid => {
      if (!getState().siteListPage.shouldCancel) {
        dispatch({ type: PROCESSING_SITES_ONTO, payload: uuid });
        return dispatch(deleteSite(uuid)).catch(e => ({ uuid, error: e }));
      }
      return Promise.resolve({ uuid, error: 'deleteCanceled' });
    })
    .then(onSuccess)
    .catch(onFail);
};

export const geocodeSites = (googleMaps, sites) => (dispatch, getState) => {
  const onSuccess = results => {
    dispatch({ type: GEOCODE_SITES_SUCCESS });
    return results;
  };
  const onFail = e => {
    dispatch({ type: GEOCODE_SITES_FAIL });
    throw e;
  };

  dispatch({ type: GEOCODE_SITES_START });
  dispatch({ type: PROCESSING_SITES_ONTO, payload: null });

  return bluebird
    .mapSeries(sites, site => {
      const { id: uuid, address1, country } = site;
      const completeAddress = getCompleteAddress(site);
      if (!address1) {
        return Promise.resolve({ uuid, error: 'geocodeNoAddress' });
      }
      if (!getState().siteListPage.shouldCancel) {
        dispatch({ type: PROCESSING_SITES_ONTO, payload: uuid });
        return geocodeSite(googleMaps, completeAddress, country)
          .then(geocoded =>
            dispatch(
              updateSite({
                id: uuid,
                longitude: geocoded.lng(),
                latitude: geocoded.lat(),
              }),
            ),
          )
          .catch(e => Promise.resolve({ uuid, error: e }));
      }
      return Promise.resolve({ uuid, error: 'geocodeCanceled' });
    })
    .then(onSuccess)
    .catch(onFail);
};

export const refreshSites = () => {
  return {
    type: SITE_LIST_REFRESH,
  };
};

export const cancel = () => {
  return {
    type: SITE_LIST_CANCEL_ACTION,
  };
};

export function setIsFormDirty(isDirty) {
  return {
    type: IS_FORM_DIRTY,
    payload: isDirty,
  };
}

export function setShowChickenExit(showChickenExit) {
  return {
    type: SHOW_CHICKEN_EXIT,
    payload: showChickenExit,
  };
}
